import { useState, useEffect, useRef } from 'react';
import { LIGHT, MUTE, FAINT, GHOST, GOLD, EMBER, BASE, SURFACE, BODY, MONO } from '../lib/constants';
import { Horizon, Btn, Mo, Bd, Ic } from './shared';
import * as api from '../lib/api';

export function CoachScreen({ user, navigate }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `coach-${Date.now()}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    setMsgs([{ role: 'ai', text: `Hey ${user?.name || 'there'}! I'm your Dettio Coach. Ask me anything about your debt strategy, or tap the analysis button above for a deep dive into your spending habits.` }]);
  }, [user?.name]);

  useEffect(() => { scrollRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input;
    setMsgs(p => [...p, { role: 'user', text: userMsg }]);
    setInput('');
    setLoading(true);
    try {
      const res = await api.sendCoachMessage(sessionId, userMsg);
      setMsgs(p => [...p, { role: 'ai', text: res.data.text }]);
    } catch {
      setMsgs(p => [...p, { role: 'ai', text: "Based on your current numbers, I'd recommend staying focused on your target — it's so close to done." }]);
    }
    setLoading(false);
  };

  return (
    <div data-testid="coach-screen" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)', position: 'relative' }}>
      <Horizon intensity={0.4} />
      <div style={{ flex: 1, padding: '16px 28px', overflowY: 'auto', position: 'relative', zIndex: 1 }}>
        {msgs.map((m, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
            {m.role === 'ai' && (
              <div 
                className={loading && i === msgs.length - 1 ? 'coach-avatar-pulse' : ''}
                style={{ 
                  width: 32, 
                  height: 32, 
                  background: `linear-gradient(135deg,${GOLD},${EMBER})`, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  marginRight: 8, 
                  flexShrink: 0,
                  borderRadius: 8,
                  position: 'relative'
                }}
              >
                {Ic.flame(14, BASE)}
              </div>
            )}
            <div 
              className={m.role === 'ai' && i === msgs.length - 1 ? 'soft-b' : ''} 
              style={{ 
                maxWidth: '80%', 
                padding: '12px 16px',
                background: m.role === 'user' 
                  ? 'rgba(232, 168, 73, 0.12)' 
                  : 'rgba(255, 255, 255, 0.03)',
                backdropFilter: 'blur(15px)',
                WebkitBackdropFilter: 'blur(15px)',
                border: m.role === 'user' 
                  ? '1px solid rgba(232, 168, 73, 0.2)' 
                  : '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 12,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                fontFamily: BODY, 
                fontSize: 13, 
                color: 'var(--light)', 
                lineHeight: 1.6
              }}
            >
              {m.text}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', marginBottom: 10 }}>
            <div 
              className="coach-avatar-pulse" 
              style={{ 
                width: 32, 
                height: 32, 
                background: `linear-gradient(135deg,${GOLD},${EMBER})`, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                marginRight: 8, 
                flexShrink: 0,
                borderRadius: 8
              }}
            >
              {Ic.flame(14, BASE)}
            </div>
            <div 
              className="pulse-a" 
              style={{ 
                padding: '12px 16px', 
                background: 'rgba(255, 255, 255, 0.03)',
                backdropFilter: 'blur(15px)',
                WebkitBackdropFilter: 'blur(15px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 12,
                fontFamily: MONO, 
                fontSize: 11, 
                color: 'var(--mute)' 
              }}
            >
              thinking...
            </div>
          </div>
        )}
        <div ref={scrollRef} />
      </div>
      <div style={{ padding: '12px 28px 20px', position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input 
            value={input} 
            onChange={e => setInput(e.target.value)} 
            onKeyDown={e => e.key === 'Enter' && send()} 
            placeholder="Ask anything about your debt..." 
            data-testid="coach-input"
            style={{ 
              flex: 1, 
              padding: '12px 16px', 
              background: 'rgba(255, 255, 255, 0.03)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)', 
              color: 'var(--light)', 
              fontFamily: BODY, 
              fontSize: 13, 
              outline: 'none', 
              borderRadius: 8 
            }} 
          />
          <button 
            onClick={send} 
            data-testid="coach-send-btn" 
            style={{ 
              padding: '12px 16px', 
              background: GOLD, 
              border: 'none', 
              cursor: 'pointer',
              borderRadius: 8,
              transition: 'transform 0.2s ease'
            }}
          >
            {Ic.send(16, BASE)}
          </button>
        </div>
      </div>
    </div>
  );
}
